if (!customElements.get('cart-quick-view')) {
  const MOBILE_BREAKPOINT = 750;
  const CLOSE_DURATION = 320;
  const SNAP_CLOSE_PERCENT = 0.5;
  const SNAP_CLOSE_VELOCITY = 1.2;

  customElements.define(
    'cart-quick-view',
    class CartQuickView extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('.cart-quick-view__content');
        this.panel = this.querySelector('.cart-quick-view__panel');
        this.scrim = this.querySelector('.cart-quick-view__scrim');
        this.openedBy = null;
        this.drag = null;

        const grip = this.querySelector('.cart-quick-view__grip');
        if (grip) grip.addEventListener('pointerdown', this.onDragStart.bind(this));
        this.onDragMove = this.onDragMove.bind(this);
        this.onDragEnd = this.onDragEnd.bind(this);

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-quick-view-close]')) this.close();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
        });

        this.addEventListener('click', this.onOptionClick.bind(this));
        this.addEventListener('bundle-picker:change', this.onBundleChange.bind(this));

        this.onDocumentClick = this.onDocumentClick.bind(this);
        document.addEventListener('click', this.onDocumentClick);

        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.close(true));
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        if (this.unsubscribeCartUpdate) this.unsubscribeCartUpdate();
      }

      onDocumentClick(event) {
        const opener = event.target.closest('[data-quick-view-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      }

      open(opener) {
        const id = opener.dataset.quickViewOpen;
        const template = document.querySelector(`template[data-quick-view="${id}"]`);
        if (!template) return;

        if (this.hideTimer) {
          clearTimeout(this.hideTimer);
          this.hideTimer = null;
        }

        this.openedBy = opener;
        this.dataset.product = id;
        this.content.replaceChildren(template.content.cloneNode(true));
        const json = this.querySelector('[data-quick-view-variants]');
        this.variants = json ? JSON.parse(json.textContent) : [];
        this.ready = false;
        this.syncSelection();
        this.ready = true;
        this.removeAttribute('hidden');
        document.body.classList.add('overflow-hidden');
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
        trapFocus(this, this.panel);
      }

      close(fromCartUpdate = false) {
        if (this.hasAttribute('hidden')) return;

        this.classList.remove('is-open');
        this.panel.style.transition = '';
        this.panel.style.transform = '';
        if (this.hideTimer) clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => {
          this.setAttribute('hidden', '');
          this.content.replaceChildren();
          delete this.dataset.product;
        }, CLOSE_DURATION);
        this.variants = null;

        if (!document.querySelector('cart-drawer.active')) {
          document.body.classList.remove('overflow-hidden');
        }

        if (fromCartUpdate) {
          this.openedBy = null;
          removeTrapFocus();
          setTimeout(() => {
            const drawerClose = document.querySelector('cart-drawer .drawer__close');
            if (drawerClose) drawerClose.focus();
          });
          return;
        }

        const focusTarget =
          this.openedBy && this.openedBy.isConnected
            ? this.openedBy
            : document.querySelector('cart-drawer .drawer__close');
        this.openedBy = null;

        const openDrawer = document.querySelector('cart-drawer.active');
        if (openDrawer) {
          const containerToTrapFocusOn = openDrawer.classList.contains('is-empty')
            ? openDrawer.querySelector('.drawer__inner-empty')
            : openDrawer.querySelector('.drawer__inner') || openDrawer;
          trapFocus(containerToTrapFocusOn, focusTarget || openDrawer.querySelector('.drawer__close'));
          return;
        }

        removeTrapFocus(focusTarget);
      }

      onDragStart(event) {
        if (window.innerWidth >= MOBILE_BREAKPOINT) return;
        this.drag = { startY: event.clientY, currentY: event.clientY, startTime: Date.now() };
        this.panel.style.transition = 'none';
        window.addEventListener('pointermove', this.onDragMove);
        window.addEventListener('pointerup', this.onDragEnd);
      }

      onDragMove(event) {
        if (!this.drag) return;
        this.drag.currentY = event.clientY;
        const delta = Math.max(0, event.clientY - this.drag.startY);
        this.panel.style.transform = `translateY(${delta}px)`;
      }

      onDragEnd() {
        window.removeEventListener('pointermove', this.onDragMove);
        window.removeEventListener('pointerup', this.onDragEnd);
        if (!this.drag) return;

        const delta = Math.max(0, this.drag.currentY - this.drag.startY);
        const elapsed = Date.now() - this.drag.startTime;
        const velocity = elapsed > 0 ? delta / elapsed : 0;
        this.drag = null;

        this.panel.style.transition = '';
        if (delta > this.panel.offsetHeight * SNAP_CLOSE_PERCENT || velocity > SNAP_CLOSE_VELOCITY) {
          this.close();
          return;
        }
        this.panel.style.transform = '';
      }

      onOptionClick(event) {
        const value = event.target.closest('[data-option-position]');
        if (value && this.variants) {
          const row = value.closest('.cart-quick-view__option');
          row.querySelectorAll('[data-option-position]').forEach((el) => {
            el.classList.toggle('is-selected', el === value);
          });
          this.resolveAfter(Number(value.dataset.optionPosition));
          this.syncSelection();
          return;
        }

        const thumb = event.target.closest('.cart-quick-view__thumb');
        if (thumb) this.showMedia(Number(thumb.dataset.mediaPosition));
      }

      resolveAfter(position) {
        const rows = Array.from(this.querySelectorAll('.cart-quick-view__option'));
        const selection = this.selection;
        const startsEmpty = this.querySelector('[data-start-empty]') !== null;

        for (let index = position + 1; index < rows.length; index++) {
          if (selection[index] === null) continue;
          const candidates = this.variants.filter(
            (variant) =>
              variant.available &&
              variant.options.every((option, i) => i >= index || selection[i] === null || option === selection[i])
          );
          if (!candidates.length) return;
          if (candidates.some((variant) => variant.options[index] === selection[index])) continue;

          if (startsEmpty) {
            rows[index].querySelectorAll('[data-option-position]').forEach((el) => el.classList.remove('is-selected'));
            selection[index] = null;
            continue;
          }

          const fallback = Array.from(rows[index].querySelectorAll('[data-option-position]')).find((el) =>
            candidates.some((variant) => variant.options[index] === el.dataset.optionValue)
          );
          if (!fallback) return;

          rows[index].querySelectorAll('[data-option-position]').forEach((el) => {
            el.classList.toggle('is-selected', el === fallback);
          });
          selection[index] = fallback.dataset.optionValue;
        }
      }

      get selection() {
        return Array.from(this.querySelectorAll('.cart-quick-view__option')).map((row) => {
          const selected = row.querySelector('[data-option-position].is-selected');
          return selected ? selected.dataset.optionValue : null;
        });
      }

      syncSelection() {
        if (!this.variants || !this.variants.length) return;

        const selection = this.selection;
        const match = this.variants.find((variant) =>
          variant.options.every((option, index) => option === selection[index])
        );

        this.querySelectorAll('[data-option-position]').forEach((el) => {
          const index = Number(el.dataset.optionPosition);
          const reachable = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === el.dataset.optionValue &&
              variant.options.every((option, i) => i >= index || selection[i] === null || option === selection[i])
          );
          el.classList.toggle('is-unavailable', !reachable);
          el.toggleAttribute('aria-disabled', !reachable);
        });

        const idInput = this.querySelector('[name="id"]');
        const submit = this.querySelector('[type="submit"]');
        const label = submit.querySelector('span');
        const price = this.querySelector('[data-quick-view-price]');

        if (!match) {
          idInput.value = '';
          submit.setAttribute('disabled', 'disabled');
          label.textContent = submit.dataset.labelChoose;
          this.announce(label.textContent);
          return;
        }

        if (price) price.textContent = match.price;
        this.showMedia(match.media);

        if (match.available) {
          idInput.value = match.id;
          submit.removeAttribute('disabled');
          label.textContent = submit.dataset.labelAdd;
        } else {
          idInput.value = '';
          submit.setAttribute('disabled', 'disabled');
          label.textContent = submit.dataset.labelSoldOut;
        }

        this.announce(`${match.price} ${label.textContent}`);
      }

      onBundleChange(event) {
        const { valid, price } = event.detail;
        const submit = this.querySelector('[type="submit"]');
        const label = submit.querySelector('span');
        const priceEl = this.querySelector('[data-quick-view-price]');

        if (priceEl && price) priceEl.textContent = price;
        label.textContent = valid ? submit.dataset.labelAdd : submit.dataset.labelChoose;
        this.announce(valid ? `${price} ${label.textContent}` : label.textContent);
      }

      announce(text) {
        if (!this.ready) return;
        const status = this.querySelector('[data-quick-view-status]');
        if (status) status.textContent = text;
      }

      showMedia(position) {
        if (!position) return;
        const target = this.querySelector(`[data-media-position="${position}"]`);
        if (!target) return;
        this.querySelectorAll('[data-media-position]').forEach((el) => {
          el.classList.toggle('is-active', Number(el.dataset.mediaPosition) === position);
        });
      }
    }
  );
}
